import React, { useState } from "react";
import { motion } from "framer-motion";

const RegistrationFormBuilder = ({ fields, onChange }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);

  const availableFields = [
    { id: "full_name", label: "Full Name", type: "text", required: true, icon: "👤" },
    { id: "email", label: "Email", type: "email", required: true, icon: "📧" },
    { id: "phone", label: "Phone Number", type: "tel", required: false, icon: "📱" },
    { id: "company", label: "Company", type: "text", required: false, icon: "🏢" },
    { id: "job_title", label: "Job Title", type: "text", required: false, icon: "💼" },
    { id: "dietary", label: "Dietary Requirements", type: "textarea", required: false, icon: "🍽️" },
    { id: "tshirt", label: "T-Shirt Size", type: "select", required: false, icon: "👕",
      options: ["XS", "S", "M", "L", "XL", "XXL"] },
    { id: "linkedin", label: "LinkedIn Profile", type: "url", required: false, icon: "💼" },
    { id: "twitter", label: "Twitter Handle", type: "text", required: false, icon: "🐦" },
    { id: "questions", label: "Questions/Comments", type: "textarea", required: false, icon: "💬" },
  ];

  const handleDragStart = (e, index, source) => {
    setDraggedIndex({ index, source });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (!draggedIndex) return;

    if (draggedIndex.source === "available") {
      // Adding new field from available fields
      const fieldToAdd = availableFields[draggedIndex.index];
      const newFields = [...fields];
      newFields.splice(dropIndex, 0, { ...fieldToAdd });
      onChange(newFields);
    } else {
      // Reordering existing fields
      const newFields = [...fields];
      const [removed] = newFields.splice(draggedIndex.index, 1);
      newFields.splice(dropIndex, 0, removed);
      onChange(newFields);
    }

    setDraggedIndex(null);
  };

  const handleRemoveField = (index) => {
    const newFields = fields.filter((_, i) => i !== index);
    onChange(newFields);
  };

  const handleToggleRequired = (index) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], required: !newFields[index].required };
    onChange(newFields);
  };

  const usedFieldIds = new Set(fields.map(f => f.id));
  const unusedFields = availableFields.filter(f => !usedFieldIds.has(f.id));

  return (
    <div className="grid grid-cols-2 gap-6">
      {/* Available Fields */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Available Fields</h3>
        <div className="space-y-2 bg-gray-50 rounded-lg p-4 min-h-[400px]">
          {unusedFields.map((field, index) => (
            <motion.div
              key={field.id}
              draggable
              onDragStart={(e) => handleDragStart(e, availableFields.indexOf(field), "available")}
              whileHover={{ scale: 1.02 }}
              className="bg-white p-3 rounded-lg border-2 border-gray-200 cursor-move hover:border-blue-400 hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{field.icon}</span>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 text-sm">{field.label}</div>
                  <div className="text-xs text-gray-500 capitalize">{field.type}</div>
                </div>
                <div className="text-gray-400 text-sm">⋮⋮</div>
              </div>
            </motion.div>
          ))}
          {unusedFields.length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm">
              All fields added
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          💡 Drag fields to the form to add them
        </p>
      </div>

      {/* Form Preview */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          Registration Form Preview
        </h3>
        <div
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, fields.length)}
          className="space-y-3 bg-white rounded-lg border-2 border-dashed border-gray-300 p-4 min-h-[400px]"
        >
          {fields.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <div className="text-4xl mb-2">📋</div>
              <p className="text-sm">Drag fields here to build your form</p>
            </div>
          )}

          {fields.map((field, index) => (
            <div
              key={`${field.id}-${index}`}
              draggable
              onDragStart={(e) => handleDragStart(e, index, "form")}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200 hover:border-blue-400 cursor-move group transition-all"
            >
              <div className="flex items-start gap-3">
                <span className="text-xl mt-1">{field.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="font-medium text-gray-900 text-sm">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                  </div>

                  {/* Field Preview */}
                  {field.type === "textarea" ? (
                    <textarea
                      placeholder={`Enter ${field.label.toLowerCase()}...`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      rows={3}
                      disabled
                    />
                  ) : field.type === "select" ? (
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      disabled
                    >
                      <option>Select {field.label.toLowerCase()}...</option>
                      {field.options?.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type}
                      placeholder={`Enter ${field.label.toLowerCase()}...`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      disabled
                    />
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleToggleRequired(index)}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      field.required
                        ? "bg-red-100 text-red-700 hover:bg-red-200"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    title={field.required ? "Make optional" : "Make required"}
                  >
                    {field.required ? "Required" : "Optional"}
                  </button>
                  <button
                    onClick={() => handleRemoveField(index)}
                    className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs font-medium hover:bg-red-200 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          💡 Drag to reorder • Click to toggle required/optional • Click remove to delete
        </p>
      </div>
    </div>
  );
};

export default RegistrationFormBuilder;
